import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import usePwaPullToReload, { usePwaPullToReloadEnabled } from '../../hooks/usePwaPullToReload';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { PULL_TO_RELOAD_THRESHOLD_PX } from '../../utils/pwaPullToReload';

const PwaPullToReload = () => {
  const { t } = useTranslation();
  const enabled = usePwaPullToReloadEnabled();
  const { pullDistance, reloading, armed } = usePwaPullToReload({ enabled });

  if (!enabled || (pullDistance <= 0 && !reloading)) {
    return null;
  }

  const progress = Math.min(100, (pullDistance / PULL_TO_RELOAD_THRESHOLD_PX) * 100);
  const labelKey = reloading
    ? 'common.reloadingApp'
    : armed
      ? 'common.releaseToReload'
      : 'common.pullToReload';

  return (
    <Box
      aria-live="polite"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1250,
        display: 'flex',
        justifyContent: 'center',
        pt: `calc(12px + env(safe-area-inset-top, 0px))`,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: BRAND.surface,
          color: BRAND.text,
          border: `1px solid ${BRAND.line}`,
          borderRadius: BRAND_RADIUS.pill,
          boxShadow: `0 8px 24px ${brandAlpha('primary', 0.12)}`,
          transform: `translateY(${Math.max(pullDistance - 20, 0)}px)`,
          opacity: Math.min(1, pullDistance / 20),
        }}
      >
        <CircularProgress
          size={22}
          thickness={5}
          variant={reloading || armed ? 'indeterminate' : 'determinate'}
          value={progress}
          sx={{ color: BRAND.accent }}
        />
        <Typography variant="caption" sx={{ fontWeight: 600, color: BRAND.text }}>
          {t(labelKey)}
        </Typography>
      </Box>
    </Box>
  );
};

export default PwaPullToReload;
